function postRequest(url, data, target = window) {
    const form = document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('action', url);
    form.style.display = 'none';
    for (const [name, value] of Object.entries(data)) {
        const entry = document.createElement('input');
        entry.setAttribute('type', 'hidden');
        entry.setAttribute('name', name);
        entry.setAttribute('value', typeof value === "string" ? value : JSON.stringify(value));
        form.appendChild(entry);
    }
    if (target !== window) {
        target.name = target.name || generateWindowName(url);
        form.setAttribute('target', target.name);
    }
    document.body.appendChild(form);
    form.submit();
    setTimeout(() => form.remove(), 300);
}
function generateWindowName(url) {
    return `ten31-pass_${url}_${Date.now()}`;
}
function isIOS() {
    // taken from @nimiq/utils/BrowserDetection
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

// Similar in spirit to Nimiq Hub's request behaviors but different in implementation because the Hub's request
var ResponseType;
(function (ResponseType) {
    ResponseType["POST_MESSAGE"] = "post-message";
    ResponseType["REDIRECT"] = "redirect";
    ResponseType["IMMEDIATE_REDIRECT"] = "immediate-redirect";
})(ResponseType || (ResponseType = {}));
// Only for internal use. To the outside, we throw errors.
var ResponseStatus;
(function (ResponseStatus) {
    ResponseStatus["Success"] = "Success";
    ResponseStatus["Error"] = "Error";
    ResponseStatus["InvalidRequest"] = "InvalidRequest";
    ResponseStatus["Unknown"] = "Unknown";
})(ResponseStatus || (ResponseStatus = {}));
class RedirectBehavior {
    endpoint;
    static getRecoverableState(requestId) {
        return JSON.parse(window.sessionStorage[RedirectBehavior.STORAGE_KEY] || '{}')[requestId] || null;
    }
    static setRecoverableState(requestId, recoverableState) {
        window.sessionStorage[RedirectBehavior.STORAGE_KEY] = JSON.stringify({
            ...JSON.parse(window.sessionStorage[RedirectBehavior.STORAGE_KEY] || '{}'),
            [requestId]: recoverableState,
        });
    }
    static getRedirectResponse(event, requiredKeys = [], // status always required, others only for status === 'Success'
    optionalKeys = [], origin) {
        if (origin && (!document.referrer || new URL(document.referrer).origin !== origin))
            return null;
        if (!requiredKeys.includes('status')) {
            requiredKeys.push('status');
        }
        const responseId = event
            + `_required:${requiredKeys.map((key) => key.toString()).sort()}`
            + (optionalKeys.length ? `_optional:${optionalKeys.map((key) => key.toString()).sort()}` : '');
        // Try to find response in location.search or location.hash
        for (let query of [location.search, location.hash]) {
            query = query.substring(1); // remove query ? or fragment #
            if (!query)
                continue;
            const parsedQuery = new URLSearchParams(query);
            if (parsedQuery.get('event') !== event)
                continue;
            // Cleaned up query where we'll remove the redirect response but leave all other potential parameters as
            // they are by using string replacements instead of parsedQuery.delete and then parsedQuery.toString to
            // avoid format changes of remaining parameters.
            let cleanedQuery = query.replace(/event=[^&]+&?/, '');
            // Read required and optional values
            const response = {};
            const expectedKeys = [...requiredKeys, ...optionalKeys];
            const missingRequiredKeys = new Set(requiredKeys);
            for (const [key, value] of parsedQuery) {
                for (const expectedKey of expectedKeys) {
                    if (typeof expectedKey === 'string' ? expectedKey !== key : key.match(expectedKey)?.[0] !== key) {
                        continue;
                    }
                    response[key] = value;
                    missingRequiredKeys.delete(expectedKey);
                    // Remove the entry from the cleaned query
                    cleanedQuery = cleanedQuery.replace(new RegExp(
                    // Turn string into regex by escaping regex special chars.
                    `${key}=${value}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                        // Remove & between this and the following entry
                        + '&?'), '');
                    break;
                }
            }
            cleanedQuery = cleanedQuery.replace(/&$/, ''); // remove potential leftover trailing &
            if (!response.status || (response.status === ResponseStatus.Success && missingRequiredKeys.size)) {
                throw new Error('TEN31 Pass did not return expected response.');
            }
            if (response.status !== ResponseStatus.Success) {
                // Different to popup requests, reject on any kind of error because the user can not retry anymore after
                // the redirect. With the current TEN31 Pass implementation however, redirects are only executed for
                // successful requests anyways.
                throw new Error(`TEN31 Pass rejected request with error: ${response.status}`, {
                    cause: new Error(response.status),
                });
            }
            // Cache response and set new url with removed redirect response
            history.replaceState({
                ...history.state,
                [RedirectBehavior.STORAGE_KEY]: {
                    ...history.state?.[RedirectBehavior.STORAGE_KEY],
                    [responseId]: response,
                },
            }, '', location.href.replace(query, cleanedQuery));
            return response;
        }
        // Check for cached response
        return history.state?.[RedirectBehavior.STORAGE_KEY]?.[responseId] || null;
    }
    static STORAGE_KEY = 'ten31-pass-redirect-behavior';
    constructor(endpoint) {
        this.endpoint = endpoint;
    }
    call(request, data, options) {
        if (options && 'recoverableState' in options) {
            RedirectBehavior.setRecoverableState(options.requestId, options.recoverableState);
        }
        const url = `${this.endpoint}${request}`;
        if (data || options?.preferredResponseType) {
            postRequest(url, {
                ...data,
                ...(options?.preferredResponseType ? { preferred_response_type: options.preferredResponseType } : null),
            });
        }
        else {
            window.location.assign(url);
        }
    }
}
class PopupBehavior {
    endpoint;
    static createPopup(url) {
        const popupName = generateWindowName(url);
        const popup = window.open(url, popupName, `left=${window.innerWidth / 2 - 400},top=75,width=800,height=850,location=yes`);
        if (!popup)
            throw new Error('TEN31 Pass popup failed to open.');
        return popup;
    }
    _endpointOrigin;
    constructor(endpoint) {
        this.endpoint = endpoint;
        this._endpointOrigin = new URL(this.endpoint).origin;
    }
    call(request, data, options) {
        if (options && 'recoverableState' in options) {
            // Cache recoverable state if requested, regardless of popup and preferred response type because also popups
            // can respond via redirect if requested or as a fallback if no Javascript is available on TEN31 Pass.
            RedirectBehavior.setRecoverableState(options.requestId, options.recoverableState);
        }
        const requestUrl = `${this.endpoint}${request}`;
        const requestData = data || options?.preferredResponseType ? {
            ...data,
            ...(options?.preferredResponseType ? { preferred_response_type: options.preferredResponseType } : null),
        } : undefined;
        let popup = PopupBehavior.createPopup(requestUrl);
        if (requestData) {
            postRequest(requestUrl, requestData, popup);
        }
        let overlay;
        const overlayOptions = typeof options?.overlay === 'object'
            ? options.overlay
            : {};
        if (options?.overlay) {
            overlay = this.appendPopupOverlay(
            /* onFocusRequested */ () => {
                if (isIOS()) {
                    // iOS doesn't allow to focus the popup. We have to re-open it to bring it to the front.
                    // We don't have to unregister our close check and post message listener because they
                    // automatically work on the new popup.
                    popup.close();
                    popup = PopupBehavior.createPopup(requestUrl);
                    if (requestData) {
                        postRequest(requestUrl, requestData, popup);
                    }
                }
                else {
                    popup.focus();
                }
            }, 
            /* onCloseRequested */ () => popup.close(), overlayOptions);
        }
        if (options?.preferredResponseType !== ResponseType.POST_MESSAGE) {
            // not expecting a response
            if (overlay) {
                // Remove overlay again once the popup has been closed
                const closeCheckInterval = window.setInterval(() => {
                    if (!popup.closed)
                        return;
                    window.clearInterval(closeCheckInterval);
                    this.removeOverlay(overlay);
                }, 300);
            }
            return;
        }
        let closeCheckInterval = -1;
        let onPopupMessage;
        return new Promise((resolve, reject) => {
            onPopupMessage = (event) => {
                if (event.origin !== this._endpointOrigin || !event.data || typeof event.data !== 'object'
                    || event.data.event !== options.responseEvent)
                    return;
                delete event.data.event;
                const responseMessage = event.data;
                if (options.responseFilter
                    ? !options.responseFilter(responseMessage)
                    // By default ignore unspecified errors (e.g. got logged out or wrong totp) as user can try again.
                    : [ResponseStatus.Error, ResponseStatus.Unknown].includes(responseMessage.status)) {
                    return;
                }
                if (!responseMessage.status
                    || (responseMessage.status === ResponseStatus.Success && options.requiredKeys
                        && options.requiredKeys.some((requiredKey) => typeof requiredKey === 'string'
                            ? !(requiredKey in responseMessage)
                            : !Object.keys(responseMessage).some((key) => key.match(requiredKey)?.[0] === key)))) {
                    reject(new Error('TEN31 Pass did not return expected response.'));
                }
                else if (responseMessage.status !== ResponseStatus.Success) {
                    reject(new Error(`TEN31 Pass rejected request with error: ${responseMessage.status}`, {
                        cause: new Error(responseMessage.status),
                    }));
                }
                else {
                    // only expose expected properties
                    delete responseMessage.status;
                    resolve(responseMessage);
                }
            };
            window.addEventListener('message', onPopupMessage);
            closeCheckInterval = window.setInterval(() => {
                if (!popup.closed)
                    return;
                reject(new Error('TEN31 Pass popup closed'));
            }, 300);
            overlayOptions.onCallbackError = reject;
        }).finally(() => {
            window.removeEventListener('message', onPopupMessage);
            window.clearInterval(closeCheckInterval);
            if (!overlay)
                return;
            this.removeOverlay(overlay);
        });
    }
    appendPopupOverlay(onFocusRequested, onCloseRequested, overlayOptions) {
        // Similar to Nimiq Hub's PopupRequestBehavior
        // Define DOM-method abstractions to allow better minification
        const createElement = document.createElement.bind(document);
        const appendChild = (node, child) => node.appendChild(child);
        // Overlay background
        const overlay = createElement('div');
        overlay.id = 'ten31-pass-overlay'; // styles can be overwritten with css targeting this id
        const overlayStyle = overlay.style;
        overlayStyle.position = 'fixed';
        overlayStyle.top = '0';
        overlayStyle.right = '0';
        overlayStyle.bottom = '0';
        overlayStyle.left = '0';
        overlayStyle.background = 'rgba(31, 35, 72, 0.8)';
        overlayStyle.display = 'flex';
        overlayStyle.flexDirection = 'column';
        overlayStyle.alignItems = 'center';
        overlayStyle.justifyContent = 'space-between';
        overlayStyle.cursor = 'pointer';
        overlayStyle.color = 'white';
        overlayStyle.textAlign = 'center';
        overlayStyle.opacity = '0';
        overlayStyle.transition = 'opacity .6s ease';
        overlayStyle.zIndex = '99999';
        overlay.addEventListener('click', () => {
            try {
                onFocusRequested();
            }
            catch (e) {
                if (!overlayOptions?.onCallbackError)
                    throw e;
                overlayOptions.onCallbackError(e);
            }
        });
        // Top flex spacer
        appendChild(overlay, createElement('div'));
        // Explainer text
        const text = createElement('div');
        text.className = 'text';
        text.textContent = overlayOptions?.text
            || 'A popup has been opened,\nclick anywhere to bring it back to the front.';
        const textStyle = text.style;
        textStyle.padding = '20px';
        textStyle.fontFamily = 'Muli, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, '
            + 'Cantarell, "Helvetica Neue", sans-serif';
        textStyle.fontSize = '24px';
        textStyle.fontWeight = '600';
        textStyle.lineHeight = '40px';
        textStyle.whiteSpace = 'pre-line';
        appendChild(overlay, text);
        // Logo
        const logo = createElement('img');
        logo.className = 'logo';
        logo.src = overlayOptions?.logo
            || 'data:image/svg+xml,<svg width="150" height="30" viewBox="0 0 75 15" xmlns="http://www.w3.org/2000/svg"><path d="M1 .5H12v2.8H8v11.2H5.1V3.3H1V.5Zm21.5 0H14v14h8.4v-2.8h-5.6V8.9H21V6.1h-4.2V3.3h5.6V.5Zm46.1 0v2.8L66.8.5h-.9v4.2h.9V1.9l1.8 2.8h.8V.5h-.8Zm-5.1 0L65 4.7h-.9l-.3-.8H62l-.3.8H61L62.5.5h1Zm0 2.6L63 1.6l-.6 1.5h1.2Zm-3.3.5a1.1 1.1 0 0 1-1.1 1.1h-2V.5h1.7a1.1 1.1 0 0 1 .7 2 1.1 1.1 0 0 1 .7 1ZM58 2.2h.5a.6.6 0 0 0 .4-1 .6.6 0 0 0-.4 0H58v1Zm1.4 1.3a.6.6 0 0 0-.6-.6H58V4h.8a.6.6 0 0 0 .6-.6ZM74 .5H73L71.5 2V.5h-.8v4.2h.8V3.2l.4-.4L73 4.7h1l-1.6-2.5L74 .5ZM46 5.3 48.8.5h-9.6v2.8H44l-2.6 4.2h2.1c1.3 0 2.3 1 2.3 2.1 0 1.2-1 2.1-2.3 2.1-1 0-2.4-.6-3.5-1.8l-1.5 2.5a7.3 7.3 0 0 0 5 2.1 5 5 0 0 0 5-4.9A4.9 4.9 0 0 0 46 5.3ZM51.1.5l-1.7 2.8h2V12l2.8-4.7V.5h-3.1ZM32.4.5v8.6L27.2.5h-2.8v14h2.8V5.9l5.2 8.6h2.8V.5h-2.8Z" fill="white"/></svg>';
        logo.style.marginBottom = '56px';
        appendChild(overlay, logo);
        // Close button
        const button = createElement('div');
        button.className = 'close';
        const buttonStyle = button.style;
        button.innerHTML = '&times;';
        buttonStyle.position = 'absolute';
        buttonStyle.top = '8px';
        buttonStyle.right = '8px';
        buttonStyle.fontSize = '24px';
        buttonStyle.lineHeight = '32px';
        buttonStyle.fontWeight = '600';
        buttonStyle.width = '32px';
        buttonStyle.height = '32px';
        buttonStyle.opacity = '0.8';
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            try {
                onCloseRequested();
            }
            catch (e) {
                if (!overlayOptions?.onCallbackError)
                    throw e;
                overlayOptions.onCallbackError(e);
            }
        });
        appendChild(overlay, button);
        // The 100ms delay is not just because the DOM element needs to be rendered before it
        // can be animated, but also because it actually feels better when there is a short
        // delay between the opening popup and the background fading.
        setTimeout(() => overlay.style.opacity = '1', 100);
        return appendChild(document.body, overlay);
    }
    removeOverlay(overlay) {
        overlay.style.opacity = '0';
        setTimeout(() => document.body.removeChild(overlay), 400);
    }
}

var Endpoint;
(function (Endpoint) {
    Endpoint["MAIN"] = "https://pass.ten31.com/";
    Endpoint["TEST"] = "https://test.pass.ten31.com/";
    Endpoint["LOCAL"] = "http://localhost:8082/";
})(Endpoint || (Endpoint = {}));
// Check for redirect grant response. Do this immediately, before other code potentially changes the url, e.g. via
// history.replaceState, and also to immediately remove the redirect response artifacts from the url.
RedirectBehavior.getRedirectResponse('grant-response', [/^grant-for-app-.+$/], [/^grant-for-service-.+$/]);
class Ten31PassApi {
    endpoint;
    _endpointOrigin;
    _redirectBehavior;
    _popupBehavior;
    static getRequestId(appId, serviceIds = []) {
        // Calculate a request id from data that is available at request time as well as after redirect responses.
        // Unfortunately this request id is not unique as all requests for the same app id and services will have the
        // same request id.
        return [appId, ...serviceIds.sort()].join('_');
    }
    constructor(endpoint) {
        endpoint = endpoint.replace(/\/?$/, '/'); // make sure there is a trailing slash
        this.endpoint = endpoint;
        this._endpointOrigin = new URL(endpoint).origin;
        this._redirectBehavior = new RedirectBehavior(endpoint);
        this._popupBehavior = new PopupBehavior(endpoint);
    }
    /**
     * Open TEN31 Pass's signup page, either in a popup or by redirecting the page.
     * Because we can not determine, whether a user signed up, this method returns void.
     * This call does not support a recoverable redirect state, because TEN31 Pass does not redirect back from the
     * signup flow.
     */
    signup(asPopup = true) {
        if (asPopup) {
            this._popupBehavior.call('signup');
        }
        else {
            this._redirectBehavior.call('signup');
        }
    }
    requestGrants(appId, services = [], asPopup = true, { preferredResponseType = asPopup ? ResponseType.POST_MESSAGE : ResponseType.REDIRECT, redirectRecoverableState, popupOverlay = asPopup, } = {}) {
        // convert our more user-friendly request format into ten31's
        const request = {
            app: appId,
            services: services.reduce((convertedServices, { serviceId, usages }) => {
                if (serviceId in convertedServices)
                    throw new Error('TEN31 Pass request invalid');
                convertedServices[serviceId] = (usages || []).reduce((convertedUsages, { usageId, parameters }) => {
                    if (usageId in convertedUsages)
                        throw new Error('TEN31 Pass request invalid');
                    convertedUsages[usageId] = parameters || {};
                    return convertedUsages;
                }, {});
                return convertedServices;
            }, {}),
        };
        const recoverableStateOptions = redirectRecoverableState !== undefined ? {
            requestId: Ten31PassApi.getRequestId(appId, services.map(({ serviceId }) => serviceId)),
            recoverableState: redirectRecoverableState,
        } : {};
        if (asPopup) {
            if (preferredResponseType === ResponseType.POST_MESSAGE) {
                // Returns the grant response promise
                return this._popupBehavior.call('grants/request', request, {
                    preferredResponseType,
                    responseEvent: 'grant-response',
                    requiredKeys: ['app'],
                    ...recoverableStateOptions,
                    overlay: popupOverlay,
                });
            }
            else {
                // Returns void
                return this._popupBehavior.call('grants/request', request, {
                    preferredResponseType,
                    ...recoverableStateOptions,
                    overlay: popupOverlay,
                });
            }
        }
        else {
            this._redirectBehavior.call('grants/request', request, {
                preferredResponseType: preferredResponseType,
                ...recoverableStateOptions,
            });
        }
    }
    /**
     * Check for a GrantResponse received via redirect, for requests that redirected the page instead of using a popup.
     */
    getRedirectGrantResponse() {
        const redirectResponse = RedirectBehavior.getRedirectResponse('grant-response', [/^grant-for-app-.+$/], [/^grant-for-service-.+$/], this._endpointOrigin);
        if (!redirectResponse)
            return null;
        // Convert redirect response to GrantResponse
        let appId;
        let appGrant;
        const serviceGrants = {}; // map of service id -> service grant id
        for (const [key, value] of Object.entries(redirectResponse)) {
            if (key.startsWith('grant-for-app-')) {
                appId = key.replace('grant-for-app-', '');
                appGrant = value;
            }
            else if (key.startsWith('grant-for-service-')) {
                serviceGrants[key.replace('grant-for-service-', '')] = value;
            }
        }
        // Recover state
        const requestId = Ten31PassApi.getRequestId(appId, Object.keys(serviceGrants));
        const recoveredState = RedirectBehavior.getRecoverableState(requestId);
        return {
            response: { app: appGrant, services: serviceGrants },
            recoveredState,
        };
    }
    /**
     * Fetch info about an app in the TEN31 Pass database.
     * Deactivated apps are reported as null.
     */
    async getAppInfo(appId) {
        return this._fetchData(`api/public/app/${appId}`);
    }
    /**
     * Fetch info about a service and associated supported service usages in the TEN31 Pass database.
     * Deactivated services are reported as null. Deactivated service usages are omitted.
     */
    async getServiceInfo(serviceId) {
        return this._fetchData(`api/public/service/${serviceId}`);
    }
    /**
     * Fetch info about an app grant and the associated app as well as the user who granted the app access.
     * Expired / deactivated grants and grants for deactivated apps are reported as null.
     */
    async getAppGrantInfo(appGrantId) {
        return this._fetchData(`api/public/grant/app/${appGrantId}`);
    }
    async getServiceGrantInfo(serviceGrantId, serviceApiKey) {
        return this._fetchData(`api/public/grant/service/${serviceGrantId}`, serviceApiKey);
    }
    /**
     * Consume service usage grants of a service grant, optionally adding arbitrary metadata like oasis contract id,
     * swap addresses, etc.
     * The call terminates with null, if the service grant's associated service is deactivated, and throws for invalid
     * service api keys, usage grants not associated with the service grant identified by serviceGrantId, or if
     * attempting to consume a usage grant which has already been consumed.
     * When submitting the api key for your registered service, be sure that it can safely be used in your code without
     * being leaked.
     */
    async consumeServiceGrant(serviceGrantId, usageGrants, serviceApiKey) {
        return this._fetchData(`api/public/grant/service/${serviceGrantId}/consume`, serviceApiKey, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            // convert our more user-friendly request format into ten31's
            body: JSON.stringify(usageGrants.reduce((convertedUsageGrants, { usageGrantId, metadata }) => {
                if (usageGrantId in convertedUsageGrants)
                    throw new Error('TEN31 Pass request invalid');
                convertedUsageGrants[usageGrantId] = metadata || {};
                return convertedUsageGrants;
            }, {})),
        });
    }
    async _fetchData(path, serviceApiKey, options = {}) {
        try {
            const response = await fetch(this.endpoint + path, {
                ...options,
                headers: {
                    ...options.headers,
                    ...(serviceApiKey ? { 'X-Service-Api-Key': serviceApiKey } : null),
                }
            });
            if (response.status === 404)
                return null;
            if (!response.ok)
                throw new Error(`${response.status}: ${response.statusText}`);
            return await response.json();
        }
        catch (e) {
            throw new Error(`TEN31 Pass request to ${path} failed: ${e.message}`, { cause: e });
        }
    }
}

export { Endpoint, ResponseType, Ten31PassApi as default };
//# sourceMappingURL=index.es.js.map
